import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Icon from '@/bit/components/components.icon-glyph';
import { COLOR_DICTIONARY } from '@gcloud-npm/utils.enums.colors';
import Text, {
  TEXT_COLOR,
  TEXT_SIZE,
  TEXT_KIND,
  TEXT_DISPLAY,
} from '@/bit/components/components.text';
import Input, { COMPONENT_THEME } from '@/bit/components/components.forms.input';
import {
  sendTaggingInfo,
  doScroll,
  SELECT_STYLE,
  SEARCHING_TEXT,
  NO_OPTIONS_TEXT,
} from './utils';
import Close from './close';

const Selector = ({
  className,
  label,
  id,
  name,
  defaultValue,
  error,
  options,
  onSelect,
  onChangeText,
  isSearchable = true,
  type,
  onBlur,
  searching = false,
  info = [],
  loaded = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [iconDirection, setIconDirection] = useState('CheuronDown');
  const selectRef = useRef(0);
  const selectorContainerRef = useRef(null);

  const taggingInfo = {
    componentVariation: 'all',
    componentProduct: '',
    componentName: 'select',
    componentContent: name,
  };
  const handleToggle = (event) => {
    event.preventDefault();
    setIsOpen(!isOpen);
    setIconDirection(isOpen ? 'CheuronDown' : 'CheuronUp');
    if (isOpen) sendTaggingInfo(taggingInfo, 'focus');
  };

  const handleSelectOption = (index) => {
    sendTaggingInfo(taggingInfo, 'select-option');
    setSelectedOptionIndex(index);
    setSearchValue(filteredOptions[index].label);
    setIsOpen(false);
    setIconDirection('CheuronDown');
    onSelect(filteredOptions[index]);
  };
  const handleKeyDown = (event) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const selectedIndex = selectedOptionIndex - 1;
      if (selectedIndex >= 0) {
        setSelectedOptionIndex(selectedIndex);
        doScroll(selectRef, selectedIndex);
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      const selectedIndex = selectedOptionIndex + 1;
      if (selectedIndex < options.length) {
        setSelectedOptionIndex(selectedIndex);
        doScroll(selectRef, selectedIndex);
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if(filteredOptions[selectedOptionIndex] && filteredOptions[selectedOptionIndex].label){
        handleSelectOption(
          filteredOptions.findIndex(
            (opt) => opt.label === filteredOptions[selectedOptionIndex].label
          )
        );
      }
    }
  };

  const handleInputChange = (event) => {
    setSearchValue(event.target.value);
    setSelectedOptionIndex(null);
    setIsOpen(true);
    onChangeText(event.target.value);
    onSelect(null);
  };

  const handleRemoveSearch = () => {
    setSearchValue('');
    setSelectedOptionIndex(null);
  };

  const handleClickOut = (event) => {
    if (
      selectorContainerRef.current &&
      !selectorContainerRef.current.contains(event.target)
    ) {
      if(onBlur){
        onBlur();
      }
      setIsOpen(false);
      setIconDirection('CheuronDown');
    }
  };

  useEffect(() => {
    if (isSearchable) {
      const newFilteredOptions = options.filter((option) =>
        option.label.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFilteredOptions(newFilteredOptions);
    } else {
      setFilteredOptions(options);
    }
  }, [searchValue, options, isSearchable]);

  useEffect(() => {
    // Manejar click fuera del compo
    document.addEventListener('click', handleClickOut);
    return () => {
      document.removeEventListener('click', handleClickOut);
    };
  }, []);

  useEffect(() => {
    if (defaultValue) {
      setSearchValue(defaultValue && defaultValue.label);
      setSelectedOptionIndex(1);
    }
  }, [defaultValue]);
  return (
    <div
      ref={selectorContainerRef}
      className={`${className} selector-container`}
      onKeyDownCapture={handleKeyDown}
    >
      <div className={`selector-input-wrapper${type}`} onClick={handleToggle}>
        {type === SELECT_STYLE.AUTOCOMPLETE && (
          <div className="search-icon">
            <Icon
              id="Buscar"
              alt="Buscar"
              color={COLOR_DICTIONARY.GREY6}
              width={20}
            />
          </div>
        )}
        <Input
          className="selector__input"
          onChange={handleInputChange}
          name={name}
          id={name}
          value={searchValue}
          theme={COMPONENT_THEME.NO_BOX}
          label={label}
          readOnly={!isSearchable && type !== SELECT_STYLE.AUTOCOMPLETE}
          autoComplete="off"
        />
        <div className="chevron-icon">
          {type === SELECT_STYLE.AUTOCOMPLETE && searchValue.length >= 1 && (
            <Close handleOnClick={handleRemoveSearch} />
          )}
          {type === SELECT_STYLE.BASIC && (
            <Icon
              id={iconDirection}
              height={14}
              color={COLOR_DICTIONARY.BLUEA}
            />
          )}
        </div>
      </div>
      {isOpen && (
        <ul ref={selectRef} className="selector-options">
          {searching && (
            <li className="selector-item--info">
              <Text
                text={SEARCHING_TEXT}
                color={TEXT_COLOR.GREY4}
                size={TEXT_SIZE.CAPTIO2}
                display={TEXT_DISPLAY.DEFAULT}
              />
            </li>
          )}
          {!searching &&
            (!filteredOptions ||
              (Array.isArray(filteredOptions) &&
                filteredOptions.length === 0)) &&
            !selectedOptionIndex && 
            searchValue.length >= 1 && (
              <li className="selector-item--info">
                <Text
                  text={NO_OPTIONS_TEXT}
                  color={TEXT_COLOR.GREY4}
                  size={TEXT_SIZE.CAPTION2}
                  display={TEXT_DISPLAY.DEFAULT}
                />
              </li>
            )}
          {!searching &&
            Array.isArray(filteredOptions) &&
            filteredOptions.length > 0 &&
            filteredOptions.map((option, index) => (
              <li
                className={`selector-item ${selectedOptionIndex === index ? 'selected' : ''}`}
                key={`${option.label}-${index}`}
                onClick={() => handleSelectOption(index)}
                tabIndex={-1}
              >
                <Text
                  text={option.label}
                  color={TEXT_COLOR.GREY6}
                  size={TEXT_SIZE.CAPTION2}
                />
              </li>
            ))}
        </ul>
      )}
      {error && (
        <Text
          className="selector__error"
          text={error}
          color={TEXT_COLOR.RED}
          kind={TEXT_KIND.SPAN}
          size={TEXT_SIZE.CAPTION3}
        />
      )}
      {info &&
        Array.isArray(info) &&
        info.length > 0 &&
        info.map((textInfo, index) => {
          const key = `${id}__info--${index}`;
          return (
            <div key={key} className={!loaded ? 'skeleton skeleton-text' : ''}>
              <Text
                className="selector__error"
                text={textInfo}
                color={TEXT_COLOR.GREY5}
                kind={TEXT_KIND.SPAN}
                size={TEXT_SIZE.CAPTION3}
                cyData={`${id}__info`}
              />
            </div>
          );
        })}
      <style jsx>{`@import "styles/colors.scss";

.chevron-icon {
  position: absolute;
  top: 1.125rem;
  right: 0.875rem;
}
.search-icon {
  position: absolute;
  top: calc(50% - 0.625rem);
  left: 1rem;
}
.selector-input-wrapper-basic {
  position: relative;
  border: 0.0625rem solid $color--grey_300;
  border-radius: 0.25rem;
}
.selector-input-wrapper-autocomplete {
  position: relative;
  padding-left: 2.5rem;
  padding-right: 2.5rem;
  border: 0.0625rem solid $color--grey_300;
  border-radius: 0.25rem;
}
.selector-container {
  outline: none;
  position: relative;
  width: 100%;
}
.selector-input:focus {
  outline: none;
}

.selector-input {
  width: 100%;
  height: 3rem;
  padding-top: 1.25rem;
  padding-left: 0.625rem;
  cursor: pointer;
  font-weight: bold;
  z-index: 999;
}

.autocomplete {
  padding-left: 3.125rem;
  padding-top: 0.3125rem;
}

.selector-options {
  list-style-type: none;
  padding: 0;
  max-height: 12.5rem;
  overflow-y: auto;
  border: 0.0625rem solid $color--grey_300;
  box-shadow: 0 0.125rem 0.125rem 0.125rem $color--grey_100;
  background-color: white;
  border-radius: 0.25rem;
  cursor: pointer;
  position: absolute;
  width: 100%;
  z-index: 999;
  ::-webkit-scrollbar {
    width: 0.3125rem;
    border-radius: 0.3125rem;
  }
  /* Track */
  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 0.3125rem white;
    border-radius: 0.3125rem;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background-color: #999999;
    border-radius: 0.3125rem;
  }
}
.selector-item {
  padding: 0.625rem 0.625rem 0.625rem 1.875rem;
  background-color: white;
}
.selector-item--info {
  padding: 0.625rem 0.625rem 0.625rem 0.625rem;
  background-color: white;
  display: flex;
  justify-content: center;
}
.selector-item.selected {
  background-color: $color--grey_200;
  color: $color--movistar_black;
}
.selector-item:hover {
  background-color: $color--grey_100;
  color: $color--movistar_black;
}
.label-select {
  position: absolute;
  left: 0.75rem;
  top: 0.25rem;
  font-size: 0.75rem;
  font-family: 'Regular';
  color: $color--grey_600;
}
.label-select-focused {
  position: absolute;
  z-index: 1;
  font-family: 'Regular';
  color: $color--grey_600;
}
.selector__error {
  font-size: 0.75rem;
  margin: 0.25rem 0 0 0.75rem;
}

:global(.selector__input) {
  padding-right: 1.25rem;
}`}</style>
    </div>
  );
};

Selector.propTypes = {
  className: PropTypes.string,
};

Selector.defaultProps = {
  className: '',
};

export default Selector;
